import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getApiBaseUrl } from '../config/env';
import { SavedSession, clearSession, readSession, readSavedSession, saveSession, sessionVersion } from '../auth/session';
import { ApiError, DEFAULT_ERROR_MESSAGE, NETWORK_ERROR_MESSAGE } from './types';

export const apiClient = axios.create({ headers: { 'Content-Type': 'application/json' } });
const refreshPromises = new Map<string, Promise<SavedSession>>();
let pendingWrites = 0;
const writeSubscribers = new Set<() => void>();
export function getPendingWrites(): number { return pendingWrites; }
export function subscribePendingWrites(cb: () => void): () => void {
  writeSubscribers.add(cb);
  return () => { writeSubscribers.delete(cb); };
}
export function beginWrite(): () => void {
  pendingWrites += 1;
  writeSubscribers.forEach(cb => cb());
  let done = false;
  return () => {
    if (done) return;
    done = true;
    pendingWrites -= 1;
    writeSubscribers.forEach(cb => cb());
  };
}
type SessionRequest = InternalAxiosRequestConfig & {
  _sessionId?: string | null; _sessionToken?: string; _version?: number;
  _retry?: boolean; _finishWrite?: () => void;
};
async function withCrossTabLock<T>(name: string, fn: () => Promise<T>): Promise<T> {
  return typeof navigator.locks?.request === 'function' ? navigator.locks.request(name, fn) : fn();
}
export function performSharedTokenRefresh(id = readSession()?.id): Promise<SavedSession> {
  const initial = id ? readSavedSession(id) : null;
  if (!initial || initial.needsLogin || !initial.refreshToken) {
    return Promise.reject(new ApiError('No refresh token available', 401));
  }
  const existing = refreshPromises.get(initial.id);
  if (existing) return existing;
  const promise = withCrossTabLock('golden-hounds-auth-refresh:' + initial.id, async () => {
    const current = readSavedSession(initial.id);
    if (!current || current.needsLogin) throw new ApiError('Session unavailable', 401);
    if (current.refreshToken !== initial.refreshToken) return current;
    try {
      const response = await axios.post<{ data: { access_token: string; refresh_token: string; expires_in: number } }>(
        current.apiUrl + '/api/v1/auth/refresh', { refresh_token: current.refreshToken });
      const latest = readSavedSession(current.id);
      if (!latest || latest.needsLogin) throw new ApiError('Session unavailable', 401);
      if (latest.refreshToken !== current.refreshToken) return latest;
      const pair = response.data.data;
      const updated = { ...current, accessToken: pair.access_token, refreshToken: pair.refresh_token, expiresAt: Date.now() + pair.expires_in * 1000 };
      saveSession(updated);
      return updated;
    } catch (error) {
      // Network/server failures are retryable; only rejected credentials expire the session.
      if (!axios.isAxiosError(error) || error.response?.status === 401 || error.response?.status === 403) {
        clearSession('session-expired', current.refreshToken, current.id);
      }
      throw error;
    }
  }).finally(() => { refreshPromises.delete(initial.id); });
  refreshPromises.set(initial.id, promise);
  return promise;
}
function isStale(config?: SessionRequest): boolean {
  if (config?._version === undefined) return false;
  if (config._version !== sessionVersion()) return true;
  const session = config._sessionId ? readSavedSession(config._sessionId) : null;
  return !!config._sessionId && (!session || !!session.needsLogin);
}
apiClient.interceptors.request.use((config: SessionRequest) => {
  if (config._version === undefined) {
    const session = readSession();
    config._version = sessionVersion();
    const anonymous = config._sessionId === null;
    config._sessionId = anonymous ? null : session?.id || null;
    config._sessionToken = session?.refreshToken;
    config.baseURL = config.baseURL ?? session?.apiUrl ?? getApiBaseUrl();
    if (session && !anonymous) config.headers.Authorization = 'Bearer ' + session.accessToken;
  }
  if (isStale(config)) {
    config._finishWrite?.();
    throw new axios.CanceledError('Session changed');
  }
  if (!['get', 'head', 'options'].includes(config.method || 'get') && !config._finishWrite) config._finishWrite = beginWrite();
  return config;
}, error => { throw error; }, { synchronous: true });
apiClient.interceptors.response.use(response => {
  const config = response.config as SessionRequest;
  config._finishWrite?.();
  if (isStale(config)) throw new axios.CanceledError('Session changed');
  return response;
}, async (error: AxiosError<{ code?: string; message?: string; error?: string }>) => {
  const config = error.config as SessionRequest | undefined;
  if (isStale(config)) {
    config?._finishWrite?.();
    throw new axios.CanceledError('Session changed');
  }
  const { status, data } = error.response || {};
  const code = typeof data?.code === 'string' ? data.code : undefined;
  const message = typeof data?.message === 'string' && data.message
    ? data.message
    : typeof data?.error === 'string' && data.error ? data.error : DEFAULT_ERROR_MESSAGE;
  const authEndpoint = /\/(auth\/(login|refresh|logout)|admin\/login)(?:$|\?)/.test(config?.url || '');
  if (status === 401 && !authEndpoint && config && !config._retry && config._sessionId) {
    try {
      const updated = await performSharedTokenRefresh(config._sessionId);
      if (isStale(config)) throw new axios.CanceledError('Session changed');
      config._retry = true;
      config._sessionToken = updated.refreshToken;
      config.headers.Authorization = 'Bearer ' + updated.accessToken;
      return await apiClient(config);
    } catch (refreshError) {
      config._finishWrite?.();
      if (axios.isCancel(refreshError)) throw refreshError;
      if (readSavedSession(config._sessionId)?.needsLogin) throw new axios.CanceledError('Session requires sign-in');
      throw new ApiError(message, status, code);
    }
  }
  config?._finishWrite?.();
  if (axios.isCancel(error)) throw error;
  if (!authEndpoint && config?._sessionId && (status === 401 || status === 403)) {
    clearSession(status === 403 && code === 'ADMIN_ROLE_REQUIRED' ? 'admin-required' : 'session-expired', config._sessionToken, config._sessionId);
    throw new axios.CanceledError('Session requires sign-in');
  }
  throw new ApiError(error.response ? message : NETWORK_ERROR_MESSAGE, status, code);
});
